export interface TimelineMonth {
  key: string;
  month: number;
  year: number;
  hasStmt: boolean;
  stmtId: string | null;
}

export function stmtMonthKey(stmt: any): string | null {
  if (stmt.statementMonth && stmt.statementYear)
    return `${stmt.statementYear}-${String(stmt.statementMonth).padStart(2, "0")}`;
  if (stmt.statementPeriodEnd) {
    const d = new Date(stmt.statementPeriodEnd);
    if (!isNaN(d.getTime())) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
  if (stmt.statementPeriodStart) {
    const d = new Date(stmt.statementPeriodStart);
    if (!isNaN(d.getTime())) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
  return null;
}

export function getTimelineMonths(stmts: any[]): TimelineMonth[] {
  const keyToStmt: Record<string, any> = {};
  for (const s of stmts) {
    const k = stmtMonthKey(s);
    if (k && !keyToStmt[k]) keyToStmt[k] = s;
  }
  const keys = Object.keys(keyToStmt).sort();
  if (keys.length === 0) return [];
  const first = keys[0];
  const last = keys[keys.length - 1];
  const [fy, fm] = first.split("-").map(Number);
  const [ly, lm] = last.split("-").map(Number);
  const result: TimelineMonth[] = [];
  let y = fy,
    m = fm;
  while (y < ly || (y === ly && m <= lm)) {
    const k = `${y}-${String(m).padStart(2, "0")}`;
    result.push({ key: k, month: m, year: y, hasStmt: !!keyToStmt[k], stmtId: keyToStmt[k]?.id ?? null });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return result;
}

/**
 * Filters statements down to the months the user currently has selected.
 *
 * Undated statements (no parseable month key — e.g. NextGear receivable snapshots)
 * can't be month-filtered, so they are always included. Dated statements are included
 * only when their month is currently selected. When the user deselects every dated
 * month, dated statements drop out (zero-data state) but undated ones still show, since
 * there is no month button to toggle them off.
 *
 * Importantly, an empty active-key set must NOT short-circuit to []: a lender whose only
 * statement is undated has an empty timeline, and a stale month selection (left over from
 * a prior dated upload that was replaced) would otherwise hide the freshly-uploaded
 * statement until a full page reload.
 */
export function filterEffectiveStmts(
  stmts: any[],
  timeline: TimelineMonth[],
  sel: Set<string> | undefined,
): any[] {
  if (!sel) return stmts;
  const activeKeys = new Set<string>(
    timeline.filter((t) => t.hasStmt && sel.has(t.key)).map((t) => t.key),
  );
  return stmts.filter((s) => {
    const k = stmtMonthKey(s);
    if (!k) return true;
    return activeKeys.has(k);
  });
}
